<script setup>
import { onMounted, onUnmounted, computed } from "vue";
import { ref, watch } from "vue";
import { useRoute } from "vue-router";
import HeaderSite from "./components/HeaderSite.vue";
import FooterSite from "./components/FooterSite.vue";
import PlayerSite from "./components/PlayerSite.vue";
import CookieBanner from "./components/CookieBanner.vue";
import ToastNotification from "./components/ToastNotification.vue";
import WelcomeAgeModal from "./components/WelcomeAgeModal.vue";
import SiteFooter from "./components/ds/SiteFooter.vue";
import {
  columnasPie,
  REDES as TODAS_LAS_REDES,
  escuchar,
} from "@/services/enlacesSitio";
import { useI18n } from "vue-i18n";
import OndaCheck from "./components/ds/OndaCheck.vue";
import { useTheme } from "@/composables/useTheme";
import { useConsentStore } from "@/stores/consentStore";
import SiteHeader from "@/components/ds/SiteHeader.vue";
import { usePlayerStore } from "@/stores/player";
import { useRadioAudio } from "@/composables/useRadioAudio";
import { useAudioLevels } from "@/composables/useAudioLevels";
import useNowPlaying from "@/composables/useNowPlaying";
import { getCurrentShow } from "@/services/currentShow";
import { formatRange } from "@/utils/timeUtils";
import { useToast } from "@/composables/useToast";
import { useCuentaStore } from "@/stores/cuenta";
import { medir, identificarVisita } from "@/services/medicion";
import { esRutaSinGrabar } from "./services/grabador";

const route = useRoute();
// Modo preview (iframe del manager): página limpia, sin chrome del sitio
const isPreview = computed(() => Boolean(route.meta?.preview));

// Las vistas del reset van a sangre: «sin contenedor centrado y sin ancho
// máximo, el contenido llega al borde real de la ventana» (DESIGN.md § Layout).
// La clase `container` de Bootstrap las encajonaba en 1512px con 48px de
// margen, y la retícula dibujada perdía el sentido: una línea que no llega al
// borde no cierra nada.
const isReset = computed(() => Boolean(route.meta?.reset));

// Si esta ruta no se graba. Ver el `rr-block` del contenido principal.
const sinGrabar = computed(() => esRutaSinGrabar(route.path));

// ── Cabecera consola ──
// Las vistas del reset llevan la cabecera nueva; las que aún no se han
// reescrito conservan la anterior. Cambiarlas todas de golpe pondría una
// cabecera del reset sobre cuerpos que todavía no lo son.
// La navegación. `soloEscritorio` marca los que NO bajan al panel móvil: la
// cabecera los pinta todos y `NavSheet` los filtra.
//
// Radar entra en el menú el 31.08.26, y solo en web. Hasta entonces la sección
// no tenía entrada a propósito —se llegaba por la puerta de la portada, por el
// buzón de «Sobre Astra FM» y por el pie—, y en el panel móvil sigue siendo así:
// allí ya tiene su celda propia abajo, con «Radar →», y ponerlo dos veces en la
// misma pantalla sobra.
//
// Cada celda lleva su icono desde el 07.09.26. Los siete estaban ya en la
// Library y ninguno es nuevo; los nodos, en
// `docs/entregas/2026-09-07-cabecera-marca-y-hover.md`.
const { t } = useI18n();

// Los textos, en el idioma de la página. Radio, Butaca y Radar no se traducen
// (Pablo, 04.11.26), pero pasan por la clave igual que las demás.
const NAV = [
  { label: t("marco.nav.home"), to: "/", icono: "home" },
  // `tambienEn`: la ficha del programa cuelga de Radio desde que Shows es una
  // vista suya, y el chasis tiene que decir dónde está el lector.
  // Y la página de un disco, que cuelga de la pestaña Lanzamientos (19.10.26).
  { label: t("marco.nav.radio"), to: "/radio", icono: "radio", tambienEn: ["/shows/", "/lanzamientos/"] },
  { label: t("marco.nav.actualidad"), to: "/actualidad", icono: "newspaper-outline" },
  // 🔴 Aquí estaba «Shows». **Se retira el 14.09.26**: pasa a ser una vista de
  // Radio, en `/radio/shows`. La cabecera se queda en seis destinos y el menú
  // móvil pierde el suyo.
  // 🔴 **«TV» pasa a «BUTACA» el 21.09.26**, con la claqueta del nodo
  // `Icon/butaca 2141:172`. La sección tiene ahora dos salas —videoclips y
  // secuencias— y el nombre viejo describía solo una.
  { label: t("marco.nav.butaca"), to: "/butaca", icono: "butaca" },
  { label: t("marco.nav.radar"), to: "/radar", soloEscritorio: true, icono: "radar" },
  // 🔴 **Vuelve a «Sobre Astra FM» el 12.09.26.** Pasó un día diciendo «Sobre
  // Dynamo» —el título de la navegación cambia con la marca— y la marca volvió
  // atrás. Con ella vuelve el icono: el símbolo de Dynamo deja de ser el de esta
  // celda. Ver `DESIGN.md` § La pestaña dice «Sobre Dynamo».
  { label: t("marco.nav.sobre"), to: "/about", icono: "about" },
  // 🔴 **Donar, la última** (06.10.26, `NavItem State=destacado 2396:371`): la
  // única celda destacada, con caja de filete rojo y el corazón. Entrega
  // `docs/entregas/2026-10-06-donar.md`.
  //
  // ⚠️ `soloEscritorio` porque el móvil no está dibujado: Donar en `NavSheet`
  // está pendiente de diseño (entrega, § Pendiente).
  { label: t("marco.nav.donar"), to: "/donar", icono: "heart", destacado: true, soloEscritorio: true },
];

// El pie del reset y las redes del panel móvil salen de la misma lista, que
// vive en `services/enlacesSitio.js` desde el 18.09.26: la pidió también
// Contacto, y tener dos copias fue exactamente lo que dejó la página vieja
// apuntando a cuentas que ya no eran.
//
// Se recortan a cinco para el panel: el pie del nodo móvil tiene sitio para dos
// filas de opciones y la sexta empezaría una tercera casi vacía.
const FOOT = columnasPie();
const ESCUCHAR = escuchar();
const REDES = TODAS_LAS_REDES.slice(0, 5);

const RADIO_STATION_ID = "3";

const playerStore = usePlayerStore();
const headerShow = ref(null);
let headerShowTimer = null;

async function loadHeaderShow() {
  try {
    headerShow.value = await getCurrentShow(null, RADIO_STATION_ID);
  } catch (error) {
    headerShow.value = null;
  }
}

// 🔴 **El nombre y las horas van por separado desde el 12.09.26**, y no es un
// capricho: en la barra apretada **el que se acorta es el nombre y las horas no
// se acortan nunca**. En una sola cadena eso no se puede pedir. Decisión de
// diseño en `DESIGN.md` § La barra se rompía por la celda que no tenía tope.
const headerProgramme = computed(() => headerShow.value?.name || "");

const headerProgrammeRange = computed(() => {
  const show = headerShow.value;
  return show?.range ? formatRange(show.range) : "";
});

// 🔴 **En móvil la celda de emisión dice el programa y su género, y nada más.**
// Antes repetía `headerProgramme` —nombre con horario, que no cabe y se corta—
// y debajo el readout de consola, «Activa · Estable · hora», que es justo lo que
// se retiró de la cabecera de escritorio el 07.09 por decir poco. Encargo de
// Pablo del 11.09.26 mirándolo en un iPhone.
const headerProgrammeName = computed(() => headerShow.value?.name || "");

// 🔴 **La única excepción a `generosDe`**, que es el master que une los géneros
// de un programa y los pinta todos. Aquí va **solo el primero**, y a propósito:
// esta celda mide 142, va a una línea y recorta con puntos suspensivos, así que
// con la lista entera se leería «Post Punk, Dance Pu…», que dice MENOS que un
// género completo. Medido el 21.09.26 con los tres de Revolución Urbana.
// ── Lo que suena, para el player de la cabecera (21.10.26) ──
//
// 🔴 Entrega `2026-10-21-portada-franja-y-player-en-cabecera.md`: **lo que suena
// sube a la cabecera, en todas las pantallas**, y se despliega. Hasta ese día la
// cabecera solo sabía el programa; ahora necesita la canción, la portada y el
// progreso, así que `App.vue` lleva su propio `useNowPlaying` (su estado es por
// instancia: Radio sigue con el suyo).
const {
  nowPlaying: headerNowPlaying,
  activeTrack: headerTrack,
  albumArt: headerAlbumArt,
  updateNowPlaying: actualizarCabecera,
  cancelSongEndTimer: cancelarFinCabecera,
} = useNowPlaying(RADIO_STATION_ID);

// El reloj del tema cuenta en local entre respuestas, como en Radio: el
// servidor da `elapsed` cada treinta segundos y leerlo tal cual deja la barra
// quieta entre saltos.
const anclaSegundos = ref(0);
const anclaInstante = ref(Date.now());
const ahoraCabecera = ref(Date.now());
watch(
  () => headerNowPlaying.value?.elapsed,
  (v) => {
    anclaSegundos.value = Number(v) || 0;
    anclaInstante.value = Date.now();
    ahoraCabecera.value = Date.now();
  },
  { immediate: true },
);

const headerSonando = computed(() => {
  const song = headerTrack.value?.song || null;
  const duracion = Number(headerNowPlaying.value?.duration) || 0;
  const corrido = anclaSegundos.value + (ahoraCabecera.value - anclaInstante.value) / 1000;
  return {
    portada: headerAlbumArt.value || "",
    artista: song?.artist || "",
    cancion: song?.title || "",
    album: headerTrack.value?.albumName || "",
    programa: headerProgramme.value,
    franja: headerProgrammeRange.value,
    duracion,
    transcurrido: duracion ? Math.min(corrido, duracion) : corrido,
  };
});

let relojCabecera = null;
let sondeoCabecera = null;

const headerProgrammeGenre = computed(() => {
  const show = headerShow.value;
  return show?.generos?.[0] || show?.genre || "";
});

// 🔴 **Aquí vivían `headerReadout` y `headerReadoutShort`** —«Transmisión
// activa · Señal estable · hora»—, y con ellos un reloj que se actualizaba cada
// segundo. El readout salió de la cabecera de escritorio el 07.09.26 y de la de
// móvil el 11.09.26, así que ya no lo pinta nadie: se retira entero, temporizador
// incluido.

const currentTheme = ref(
  document.documentElement.getAttribute("data-theme") || "dark",
);

const { toggleTheme } = useTheme();

function onToggleTheme() {
  toggleTheme();
  currentTheme.value =
    document.documentElement.getAttribute("data-theme") || "dark";
}

const { initTheme } = useTheme();
const consentStore = useConsentStore();
const { showToast, toastMessage, hide } = useToast();
const WELCOME_MODAL_STORAGE_KEY = "astralab_welcome_seen_v1";
const showWelcomeModal = ref(false);

const acceptWelcomeModal = () => {
  localStorage.setItem(WELCOME_MODAL_STORAGE_KEY, "true");
  showWelcomeModal.value = false;
};

onMounted(() => {
  initTheme();
  consentStore.initFromLocalStorage();

  const hasSeenWelcome = localStorage.getItem(WELCOME_MODAL_STORAGE_KEY);
  if (!hasSeenWelcome) {
    showWelcomeModal.value = true;
  }

  loadHeaderShow();
  headerShowTimer = setInterval(loadHeaderShow, 5 * 60 * 1000);

  // Lo que suena, para el player de la cabecera: al montar, cada 30 s como red
  // de seguridad (el composable ya se relee al acabar cada tema) y el reloj del
  // tema cada segundo. En segundo plano no corre nada.
  actualizarCabecera();
  sondeoCabecera = setInterval(() => !document.hidden && actualizarCabecera(), 30 * 1000);
  relojCabecera = setInterval(() => {
    if (!document.hidden) ahoraCabecera.value = Date.now();
  }, 1000);

  // Analítica: sin sesión, la visita es `anonimo`. Con sesión el tipo lo pone
  // la store de cuenta cuando la carga, que es cuando se sabe.
  if (!cuentaStore.haySesion) identificarVisita(null);
});

onUnmounted(() => {
  if (headerShowTimer) clearInterval(headerShowTimer);
  clearInterval(sondeoCabecera);
  clearInterval(relojCabecera);
  cancelarFinCabecera?.();
  clearTimeout(minutoDeEscucha);
});

// ── La escucha, para la analítica ──
//
// Contrato de analítica §5, 13.09.26. `escucha_inicio` cuando la radio **suena**
// —el `playing` del audio—, no cuando se pulsa: entre una cosa y otra están los
// segundos del búfer, y un intento que no llega a sonar no es una escucha.
// `escucha_1min` tras un minuto seguido sonando, una vez por reproducción. Una
// reproducción empieza al pulsar y acaba al parar la radio.
//
// ⚠️ No mide la audiencia: eso lo cuenta AzuraCast con los oyentes del stream.
// Esto dice cuánta gente pulsa reproducir en la web.
const cuentaStore = useCuentaStore();
let inicioMedido = false;
let minutoMedido = false;
let minutoDeEscucha = null;

// ── El minuto se SUMA, no tiene que ser seguido ──
//
// 🔴 Encargo de Pablo, 24.09.26, con el aviso del servidor delante: «vamos con
// sumado». Hasta hoy el minuto tenía que ser **de un tirón**, y un corte del
// búfer lo devolvía a cero. En el móvil eso es casi siempre: alguien que
// escucha cinco minutos en el autobús con tres microcortes no llegaba a
// contarse **ni una vez**, porque nunca tuvo sesenta segundos limpios.
//
// Lo que se quiere saber de esa cifra es **si se queda**, no si tiene buena
// cobertura. Así que ahora se suman los segundos que de verdad ha sonado.
//
// ⚠️ **El tiempo se mide con el reloj, no con el temporizador.** Cuando alguien
// se va a otra app el navegador **congela los temporizadores** de la página,
// pero el audio sigue sonando: contar con el temporizador perdería justo el
// tiempo que más cuenta. El temporizador solo sirve para despertarnos; si
// despierta tarde, la suma ya está hecha y se apunta igual.
//
// ⚠️ Y **no se usa `currentTime` del audio**, que sería lo evidente: el
// reproductor da saltos hacia delante cuando se reengancha al directo —hasta 40
// segundos—, y esos saltos se contarían como escucha que no ocurrió.
const UN_MINUTO = 60 * 1000;
let sonandoDesde = 0;
let escuchado = 0;

function propiedadesDeEscucha() {
  const programa = headerShow.value?.name;
  return programa ? { programa } : undefined;
}

/** Lo escuchado hasta ahora, contando el tramo que esté sonando ahora mismo. */
function loEscuchado() {
  return escuchado + (sonandoDesde ? Date.now() - sonandoDesde : 0);
}

function miraSiYaEsUnMinuto() {
  clearTimeout(minutoDeEscucha);
  if (minutoMedido) return;

  const falta = UN_MINUTO - loEscuchado();
  if (falta <= 0) {
    minutoMedido = true;
    medir("escucha_1min", propiedadesDeEscucha());
    return;
  }

  // Solo se programa mientras suena: si está parado, ya lo despertará el
  // siguiente `playing`.
  if (sonandoDesde) minutoDeEscucha = setTimeout(miraSiYaEsUnMinuto, falta);
}

function alSonar() {
  if (!playerStore.isPlaying) return;
  if (!inicioMedido) {
    inicioMedido = true;
    medir("escucha_inicio", propiedadesDeEscucha());
  }
  if (!sonandoDesde) sonandoDesde = Date.now();
  miraSiYaEsUnMinuto();
}

// Un corte del búfer o una pausa **ya no rompen el minuto**: cierran el tramo
// que iba sonando y lo suman. Lo que llevaba escuchado se conserva.
function alInterrumpir() {
  clearTimeout(minutoDeEscucha);
  if (sonandoDesde) {
    escuchado += Date.now() - sonandoDesde;
    sonandoDesde = 0;
  }
}

// ── La onda de la cabecera ──
//
// La barra de emisión lleva la misma onda mínima que la parrilla, y aquí hace
// falta el análisis en TODAS las páginas, no solo donde hay reproductor: la
// cabecera está siempre.
//
// El grafo se comparte por elemento en un WeakMap, así que esta instancia no
// crea un segundo `MediaElementSource` aunque la vista de turno también pida
// niveles. Y `immediate` porque al entrar con la emisión ya sonando `isPlaying`
// no CAMBIA, y sin él la onda nacería congelada.
const { audioEl } = useRadioAudio();

// Los oídos de la analítica, en el elemento de audio de la radio. Se cambian
// de sitio si el reproductor vuelve a registrar otro elemento.
watch(
  audioEl,
  (el, anterior) => {
    if (anterior) {
      anterior.removeEventListener("playing", alSonar);
      anterior.removeEventListener("pause", alInterrumpir);
      anterior.removeEventListener("waiting", alInterrumpir);
    }
    if (el) {
      el.addEventListener("playing", alSonar);
      el.addEventListener("pause", alInterrumpir);
      el.addEventListener("waiting", alInterrumpir);
    }
  },
  { immediate: true },
);

// Parar la radio cierra la reproducción: la siguiente vuelve a contar.
watch(
  () => playerStore.isPlaying,
  (sonando) => {
    if (sonando) return;
    inicioMedido = false;
    minutoMedido = false;
    // Parar la radio cierra la reproducción: la suma vuelve a cero, porque la
    // siguiente es otra escucha y no la continuación de esta.
    escuchado = 0;
    sonandoDesde = 0;
    clearTimeout(minutoDeEscucha);
  },
);
const {
  levels: headerLevels,
  estado: estadoAudio,
  analisis: analisisAudio,
  supported: analizadorOk,
  start: startHeaderLevels,
  stop: stopHeaderLevels,
  setVolume: ganancia,
} = useAudioLevels();

// ── El volumen de la emisión ──
//
// 🔴 **Uno para toda la web** (21.10.26), el del reproductor. Se aplica en la
// ganancia del grafo de la onda, que va DESPUÉS del analizador: bajar el volumen
// no aplana la onda. Donde no hay grafo —WebKit no entrega muestras al
// analizador— se aplica en el propio elemento. Lo ajustan el player de la
// cabecera y el de Radio.
function aplicarVolumen() {
  ganancia(playerStore.volumen);
  if (audioEl.value) {
    audioEl.value.volume = analisisAudio.value === "no" ? playerStore.volumen : 1;
  }
}
watch([() => playerStore.volumen, analisisAudio, audioEl], aplicarVolumen, { immediate: true });

watch(
  [() => playerStore.isPlaying, audioEl],
  async ([sonando, el]) => {
    if (sonando && el) {
      await startHeaderLevels(el);
      // El grafo nace con la ganancia a 1: se le pone la del reproductor.
      aplicarVolumen();
    }
    else if (!sonando) stopHeaderLevels();
  },
  { immediate: true },
);

</script>

<template>
  <!-- Header sticky siempre visible -->
  <div
    v-if="!isPreview"
    class="header-sticky"
  >
    <SiteHeader
      v-if="isReset"
      :items="NAV"
      :current="route.path"
      :programme="headerProgramme"
      :programme-range="headerProgrammeRange"
      :programme-name="headerProgrammeName"
      :programme-genre="headerProgrammeGenre"
      :playing="playerStore.isPlaying"
      :levels="headerLevels"
      :analisis="analisisAudio"
      :sonando="headerSonando"
      :volumen="playerStore.volumen"
      @update:volumen="playerStore.setVolumen($event)"
      :theme="currentTheme"
      :social="REDES"
      :listen="ESCUCHAR"
      @toggle="playerStore.toggle()"
      @toggle-theme="onToggleTheme"
    />
    <HeaderSite v-else />
  </div>

  <WelcomeAgeModal
    v-if="!isPreview"
    :show="showWelcomeModal"
    @accept="acceptWelcomeModal"
  />

  <!-- Contenido principal con espacio para el header.

       🔴 **`rr-block` en las páginas donde se escriben datos.** Es la clase que
       el grabador de los mapas de calor —rrweb por dentro— entiende para no
       capturar un bloque. Lo pide el servidor en
       `PETICION_HEATMAPS_Y_PRIVACIDAD_WEB.md` (22.09.26): cuenta, verificar
       correo y restablecer contraseña quedan fuera de la grabación.

       ⚠️ **Va aquí y no en cada vista**, que son diez. Y hace falta **además**
       de no arrancar el grabador en esas rutas: una vez cargado no se puede
       parar, así que quien entra por la portada y luego va a su cuenta seguiría
       grabándose. Esto es lo que lo impide. -->
  <main
    class="main-content"
    :class="{ container: !isReset, 'rr-block': sinGrabar }"
  >
    <!-- La clave es el registro de ruta y no la dirección cuando la ruta tiene
         vistas: `/radio` y `/radio/shows` son la misma página, y con
         `route.path` se montaban dos instancias de Radio. Conmutar de vista no
         puede tocar el audio ni reiniciar nada. El comentario va aquí fuera:
         `keep-alive` solo admite un hijo y un comentario dentro lo rompe. -->
    <router-view v-slot="{ Component, route }">
      <keep-alive
        :include="['HomeView', 'RadioView']"
      >
        <component :is="Component" :key="route.name === 'radio' ? 'radio' : route.path" />
      </keep-alive>
    </router-view>
  </main>

  <!-- El pie sigue la misma regla que la cabecera: en las vistas del reset
       manda SiteFooter, en el resto el pie viejo. Hasta ahora Radio arrastraba
       el pie antiguo debajo de una página ya reescrita. -->
  <SiteFooter
    v-if="isReset"
    :columns="FOOT"
    :copyright="t('marco.pie.copyright')"
    :license="t('marco.pie.licencia')"
  />
  <FooterSite v-else-if="!isPreview" />

  <!-- Audio Player (sticky bottom) -->
  <PlayerSite v-if="!isPreview" />

  <CookieBanner v-if="!isPreview" />

  <!-- Toast Notification -->
  <ToastNotification
    v-if="!isPreview"
    :show="showToast"
    :message="toastMessage"
    @close="hide"
  />

  <!-- Solo con `?onda-check=1`. Sirve para ver en el navegador de otro por qué
       la onda no se mueve, que es justo lo que no se puede depurar a distancia. -->
  <OndaCheck
    :levels="headerLevels"
    :playing="playerStore.isPlaying"
    :estado="estadoAudio"
    :supported="analizadorOk"
    :analisis="analisisAudio"
  />
</template>

<style>
/* Header sticky siempre visible */
.header-sticky {
  position: sticky;
  top: 0;
  z-index: 100;
  width: 100%;
}

/* Contenido principal con espacio para el header */
/*
.main-content {
  min-height: calc(100vh - var(--header-height, 80px));
}
*/
/* El margen superior y el fondo los pone el bloque de abajo a cualquier
   .main-content, con o sin contenedor. En una vista del reset ese margen de
   24px abre una banda entre la cabecera sticky y el título que no existe en
   el artefacto, así que se anula. */
.main-content:not(.container) {
  margin-top: 0;
}

.container-fluid,
.main-content,
.container {
  /* Padding consistente con HomeView para mantener distancia del header */

  position: relative;
  z-index: 1;

  /* Separación de la cabecera y border radius */
  margin-top: 24px;
  border-radius: 0;

  /* Background con color del theme */
  background-color: var(--color-bg);

  .image-next-song {
    margin-left: 10px;
  }
}
.container,
.main-content.container,
.container-fluid {
  max-width: calc(100% - 24px);
  margin-left: auto;
  margin-right: auto;
}
/* Desktop horizontal spacing */
@media (min-width: 769px) {
  .container,
  .main-content.container,
  .container-fluid {
    max-width: min(calc(100% - 48px), 1512px);
    margin-left: auto;
    margin-right: auto;
  }
}

/* Asegurar que el body no tenga scroll horizontal */
body {
  overflow-x: hidden;
  position: relative;
}
</style>
